/**
 * <bundle-builder> — builder « Composez votre parure ».
 *
 * Consomme le DOM rendu par snippets/bundle-builder.liquid : une ligne par
 * composant (`.bundle-line`) avec son JSON de variantes autorisées
 * (`.bundle-line__data`), une barre récap (`.bundle-summary`) et un bouton
 * d'ajout global (`.bundle-submit`).
 *
 * Gère : résolution de variante depuis les <select>, prix de ligne promo-aware,
 * stepper de quantité, total dynamique, et synchronisation continue de la
 * composition avec le panier (chaque action sur une ligne écrit au panier).
 */

/**
 * Fenêtre de regroupement des clics : une rafale sur « + » ne part qu'en un
 * seul aller-retour panier.
 */
const BUNDLE_SYNC_DEBOUNCE_MS = 300;

class BundleBuilder extends HTMLElement {
  connectedCallback() {
    this.cartUrl = this.dataset.cartUrl || '/cart';
    // `routes.cart_add_url` ne porte pas le suffixe `.js` : on le force pour
    // obtenir la réponse JSON (et les sections re-rendues) de l'API Ajax.
    const addUrl = this.dataset.cartAddUrl || this.cartUrl.replace(/\/$/, '') + '/add';
    this.cartAddUrl = addUrl.endsWith('.js') ? addUrl : addUrl + '.js';
    const updateUrl = this.dataset.cartUpdateUrl || this.cartUrl.replace(/\/$/, '') + '/update';
    this.cartUpdateUrl = updateUrl.endsWith('.js') ? updateUrl : updateUrl + '.js';
    this.bundleTitle = this.dataset.bundleTitle || '';
    this.notifyLabel = this.dataset.labelNotify || '';
    this.soldOutLabel = this.dataset.labelSoldOut || '';

    // File d'attente des synchronisations : deux réconciliations ne doivent
    // jamais lire le panier en même temps, sinon la seconde repart d'un état
    // déjà périmé.
    this._syncChain = Promise.resolve();
    this._syncTimer = null;
    this._syncPending = false;
    this._pendingOpenDrawer = false;
    // Le drawer ne s'ouvre qu'au premier ajout : au-delà il masquerait le
    // builder pendant qu'on compose.
    this._drawerShown = false;

    this.lines = [...this.querySelectorAll('.bundle-line')]
      .map((el) => this.initLine(el))
      .filter(Boolean);

    // Visuels de la parure indexés par couleur (vide tant qu'ils ne sont pas
    // taggés `#Couleur_XXX` — on retombe alors sur ceux des composants).
    this.coverMedia = {};
    const coverDataEl = this.querySelector('.bundle-cover__data');
    if (coverDataEl) {
      try {
        this.coverMedia = JSON.parse(coverDataEl.textContent) || {};
      } catch (e) {
        this.coverMedia = {};
      }
    }

    this.summaryEl = this.querySelector('.bundle-summary');
    this.countEl = this.querySelector('[data-summary-count]');
    this.totalEl = this.querySelector('[data-summary-total]');
    this.compareEl = this.querySelector('[data-summary-compare]');
    this.submitEl = this.querySelector('.bundle-submit');

    const ds = (this.summaryEl && this.summaryEl.dataset) || {};
    this.countForms = {
      one: ds.countOne || '{count}',
      other: ds.countOther || ds.countOne || '{count}',
    };
    this.locale = ds.locale || document.documentElement.lang || 'fr';

    // Dropdowns custom (couche visuelle par-dessus les <select> natifs masqués).
    this.selectWidgets = [...this.querySelectorAll('.bundle-select')]
      .map((w) => this.initSelect(w))
      .filter(Boolean);

    this._onDocClick = (e) => {
      if (this.contains(e.target) && e.target.closest('.bundle-select')) return;
      this.selectWidgets.forEach((w) => this.closeSelect(w));
    };
    this._onKeydown = (e) => {
      if (e.key === 'Escape') this.selectWidgets.forEach((w) => this.closeSelect(w));
    };
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onKeydown);

    if (this.submitEl) {
      this.submitEl.addEventListener('click', () => this.addToCart());
    }

    this.renderTotals();
    this.restoreFromCart();
    this.watchAutomaticDiscounts();
  }

  /**
   * L'app de remises se charge de son côté et publie son API quand elle est
   * prête : à la construction du builder, elle est encore muette. On applique
   * donc les remises à son signal, ou tout de suite si elle a déjà parlé.
   */
  watchAutomaticDiscounts() {
    const apply = () => this.lines.forEach((line) => this.applyAutomaticDiscount(line));

    if (window.RegiosDOPP?.api?.v0) {
      apply();
      return;
    }

    window.addEventListener('regios-dopp:api-initialized', apply, { once: true });
  }

  disconnectedCallback() {
    if (this._onDocClick) document.removeEventListener('click', this._onDocClick);
    if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
  }

  /* ------- Dropdown custom (accessibilité : <select> natif conservé) ------- */

  initSelect(widget) {
    const select = widget.querySelector('.bundle-select__native');
    const trigger = widget.querySelector('.bundle-select__trigger');
    const list = widget.querySelector('.bundle-select__list');
    if (!select || !trigger || !list) return null;

    const w = {
      widget,
      select,
      trigger,
      list,
      options: [...list.querySelectorAll('.bundle-select__option')],
    };

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleSelect(w);
    });

    // Garde le trigger synchronisé même si le <select> natif est piloté au clavier.
    select.addEventListener('change', () => this.syncTrigger(w));

    w.options.forEach((li) => {
      li.addEventListener('click', () => {
        const value = li.dataset.value;
        if (value != null && select.value !== value) {
          select.value = value;
          // Rejoue la logique native (résolution variante + prix + totaux).
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.syncTrigger(w);
        this.closeSelect(w);
        trigger.focus();
      });
    });

    this.syncTrigger(w);
    return w;
  }

  openSelect(w) {
    this.selectWidgets.forEach((o) => {
      if (o !== w) this.closeSelect(o);
    });
    w.list.hidden = false;
    w.trigger.setAttribute('aria-expanded', 'true');
    w.widget.classList.add('is-open');
  }

  closeSelect(w) {
    w.list.hidden = true;
    w.trigger.setAttribute('aria-expanded', 'false');
    w.widget.classList.remove('is-open');
  }

  toggleSelect(w) {
    if (w.list.hidden) this.openSelect(w);
    else this.closeSelect(w);
  }

  syncTrigger(w) {
    const opt = w.select.options[w.select.selectedIndex];
    const valueEl = w.trigger.querySelector('.bundle-select__value');
    if (!valueEl || !opt) return;

    valueEl.textContent = '';

    const swatch = opt.dataset.swatch;
    if (swatch) {
      const dot = document.createElement('span');
      dot.className = 'bundle-swatch bundle-select__swatch';
      if (swatch === '#ffffff' || swatch === '#fff') dot.classList.add('bundle-swatch--white');
      dot.style.setProperty('--bundle-swatch', swatch);
      valueEl.appendChild(dot);
    }

    const label = document.createElement('span');
    label.className = 'bundle-select__value-label';
    label.textContent = opt.textContent;
    valueEl.appendChild(label);
  }

  initLine(el) {
    const dataEl = el.querySelector('.bundle-line__data');
    if (!dataEl) return null;

    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return null;
    }

    const line = {
      el,
      data,
      actionEl: el.querySelector('.bundle-line__action'),
      priceEl: el.querySelector('[data-line-price]'),
      addBtn: el.querySelector('.bundle-line__add'),
      notifyBtn: null,
      selects: [...el.querySelectorAll('.bundle-line__select')],
      stepperEl: null,
      qtyEl: null,
      qty: 0,
      variant: null,
      // Une ligne porte désormais plusieurs combinaisons : deux taies carrées *et* une
      // rectangulaire, par exemple. `variant` et `qty` ne décrivent que la combinaison
      // affichée dans les sélecteurs ; `entries` est la source de vérité de ce qui est choisi.
      // Les combinaisons non affichées ne sont pas rappelées ici : le panier latéral les
      // détaille déjà, et les redoubler sous la ligne alourdissait le composeur.
      entries: [],
    };

    // Ligne en rupture : pas de contrôles, inerte mais comptée comme ligne.
    if (!line.actionEl || !line.addBtn) {
      return line;
    }

    line.selects.forEach((select) => {
      select.addEventListener('change', () => {
        // Changer de combinaison ne touche plus à ce qui est déjà choisi : l'ancienne reste
        // dans `entries` et rejoint le récapitulatif, les sélecteurs passent simplement à la
        // suivante. C'est ce que la recette attendait — deux taies carrées puis une
        // rectangulaire doivent coexister, pas se remplacer.
        const hadQty = line.qty > 0;
        this.resolveVariant(line);
        this.renderTotals();

        // La cover suit la dernière couleur choisie, quelle que soit la ligne.
        if (line.data.colorPosition && select.dataset.optionPosition == line.data.colorPosition) {
          this.showCover(select.value, line);
        }

        // `resolveVariant` peut aussi remettre la ligne à zéro (variante
        // devenue indisponible) : il faut alors la retirer du panier.
        if (hadQty || line.qty > 0) this.requestSync();
      });
    });

    line.addBtn.addEventListener('click', () => this.addLine(line));

    this.resolveVariant(line);
    return line;
  }

  currentOptions(line) {
    const values = line.data.optionNames.map(() => null);

    if (line.selects.length) {
      line.selects.forEach((select) => {
        const pos = parseInt(select.dataset.optionPosition, 10) - 1;
        if (pos >= 0) values[pos] = select.value;
      });
    }

    return values;
  }

  resolveVariant(line) {
    const chosen = this.currentOptions(line);

    line.variant =
      line.data.variants.find(
        (v) => v.options.length === chosen.length && v.options.every((opt, i) => opt === chosen[i])
      ) || null;

    // `qty` suit la combinaison affichée, pas l'inverse : si celle-ci a déjà été ajoutée, le
    // sélecteur de quantité doit rouvrir sur son compte, sinon le bouton « Ajouter » revient.
    // À faire avant `updateAddState`, qui décide de l'un ou de l'autre en lisant `qty`.
    this.syncCurrentQty(line);

    this.renderLinePrice(line);
    this.updateAddState(line);
    this.applyAutomaticDiscount(line);
    return line.variant;
  }

  /**
   * Remises automatiques (app Regios).
   *
   * Elles ne portent ni sur la parure ni sur ses variantes, mais sur les
   * produits qui la composent : l'app ne sait pas les lire ici, car le builder
   * n'affiche pas les prix dans les balises du thème qu'elle surveille. On lui
   * demande donc le calcul, plutôt que de rejouer ses règles — elles vont du
   * simple pourcentage au « 40% sur l'article le plus cher ».
   *
   * Le résultat est écrit dans la variante, pas dans le DOM : le prix de ligne
   * et le total savent déjà afficher un prix barré, hérité des soldes
   * catalogue. Le prix d'avant remise devient le prix barré.
   *
   * Sans l'app, la méthode ne fait rien et les prix catalogue restent : le
   * builder ne dépend pas d'elle.
   */
  async applyAutomaticDiscount(line) {
    const api = window.RegiosDOPP?.api?.v0;
    const variant = line.variant;

    if (!api || !variant || !line.data.productId) return;

    // Une variante n'est interrogée qu'une fois : `resolveVariant` est rappelé
    // à chaque changement de select, y compris pour revenir sur un choix déjà vu.
    if (variant.automaticDiscountApplied) return;
    variant.automaticDiscountApplied = true;

    let result;

    try {
      result = await api.calculateDiscountedPrices({
        productId: line.data.productId,
        variantId: variant.id,
        regularPriceInCents: variant.price,
      });
    } catch (e) {
      return;
    }

    const discounted = Math.round((result?.salePrice?.amount ?? 0) * 100);
    if (!discounted || discounted >= variant.price) return;

    variant.compareAt = variant.price;
    variant.price = discounted;

    // La variante a pu changer entre-temps : on ne rafraîchit la ligne que si
    // elle affiche toujours celle qu'on vient de remiser.
    if (line.variant === variant) this.renderLinePrice(line);
    this.renderTotals();
  }

  renderLinePrice(line) {
    if (!line.priceEl) return;

    const v = line.variant;
    if (!v) {
      line.priceEl.textContent = '';
      return;
    }

    if (v.compareAt && v.compareAt > v.price) {
      const pct = Math.round((1 - v.price / v.compareAt) * 100);
      line.priceEl.textContent = '';

      const sale = document.createElement('span');
      sale.className = 'bundle-line__price-sale';
      sale.textContent = this.formatMoney(v.price);

      const compare = document.createElement('span');
      compare.className = 'bundle-line__price-compare';
      compare.textContent = this.formatMoney(v.compareAt);

      const badge = document.createElement('span');
      badge.className = 'bundle-line__discount';
      badge.textContent = '-' + pct + '%';

      line.priceEl.append(sale, compare, badge);
    } else {
      line.priceEl.textContent = this.formatMoney(v.price);
    }
  }

  updateAddState(line) {
    if (!line.addBtn) return;

    const purchasable = !!(line.variant && line.variant.available);
    line.addBtn.disabled = !purchasable;

    // Dès que la combinaison choisie est indisponible, le bouton d'ajout cède la place à l'alerte
    // de réapprovisionnement : même règle que sur une fiche produit, qui la propose sur la seule
    // variante courante sans regarder le reste du catalogue. Un bouton grisé n'expliquait rien,
    // et les pastilles restées visibles montrent de toute façon les coloris encore en stock.
    // `line.qty > 0` veut dire que le sélecteur de quantité occupe déjà la place : ni le bouton
    // d'ajout ni l'alerte ne doivent y reparaître, sans quoi les deux se superposent.
    // Une combinaison devenue indisponible ne peut pas rester choisie. Seule celle qui est
    // affichée est concernée : les autres entrées de la ligne gardent leur quantité.
    if (line.qty > 0 && !purchasable) {
      this.removeEntry(line, line.variant.id);
      this.syncCurrentQty(line);
    }

    const chosen = line.qty > 0;
    const notifiable =
      this.notifyLabel && line.data.restock !== false && !chosen && !purchasable && !!(line.variant && line.variant.id);

    this.renderNotify(line, notifiable ? String(line.variant.id) : null);
    line.addBtn.hidden = !!notifiable || chosen;
    this.renderSoldOut(line, !purchasable && !notifiable);

    // Le sélecteur de quantité n'appartient plus à la ligne mais à la combinaison affichée :
    // il reparaît quand on revient sur une combinaison déjà ajoutée, et s'efface sinon.
    if (chosen) {
      this.showStepper(line);
    } else if (line.stepperEl) {
      line.stepperEl.hidden = true;
    }
  }

  /** Entrée correspondant à une combinaison déjà choisie sur cette ligne, s'il y en a une. */
  entryFor(line, variantId) {
    return line.entries.find((entree) => entree.variant.id === variantId) || null;
  }

  removeEntry(line, variantId) {
    line.entries = line.entries.filter((entree) => entree.variant.id !== variantId);
  }

  /** Aligne `qty` sur la combinaison affichée, le reste du composant se lisant dans `entries`. */
  syncCurrentQty(line) {
    const entree = line.variant ? this.entryFor(line, line.variant.id) : null;
    line.qty = entree ? entree.qty : 0;
  }

  /** Toutes les quantités choisies sur la ligne, combinaison affichée comprise. */
  lineCount(line) {
    return line.entries.reduce((total, entree) => total + entree.qty, 0);
  }

  // L'alerte de stock renvoie vers la fiche du composant, sur la variante choisie, plutôt que
  // d'ouvrir la modale ici : le widget de Klaviyo résout son `data-item-id` dans le produit de la
  // page courante, et une variante qui n'en fait pas partie le fait échouer sur un `undefined.id`.
  // Vérifié sur la fiche du composant, où le même identifiant ouvre la modale sans erreur.
  renderNotify(line, variantId) {
    const url = line.data && line.data.url;

    if (!variantId || !url) {
      if (line.notifyBtn) {
        line.notifyBtn.remove();
        line.notifyBtn = null;
      }
      return;
    }

    const href = url + (url.includes('?') ? '&' : '?') + 'variant=' + variantId;

    if (line.notifyBtn) {
      if (line.notifyBtn.getAttribute('href') === href) return;
      line.notifyBtn.setAttribute('href', href);
      return;
    }

    const link = document.createElement('a');
    link.className = 'bundle-line__notify';
    link.setAttribute('href', href);
    link.textContent = this.notifyLabel;

    line.actionEl.appendChild(link);
    line.notifyBtn = link;
  }

  // Sans alerte possible (composant non réassorti), le bouton grisé dit pourquoi il l'est, comme
  // le bouton « En rupture » de la fiche, au lieu de garder son « Ajouter ».
  renderSoldOut(line, soldOut) {
    const [labelEl, iconEl] = line.addBtn.children;
    if (!labelEl || !this.soldOutLabel) return;

    if (line.addLabel === undefined) line.addLabel = labelEl.textContent;
    labelEl.textContent = soldOut ? this.soldOutLabel : line.addLabel;
    if (iconEl) iconEl.hidden = soldOut;
  }

  addLine(line) {
    if (!line.variant || !line.variant.available) return;

    const entree = this.entryFor(line, line.variant.id);
    if (entree) {
      entree.qty += 1;
    } else {
      line.entries.push({ variant: line.variant, qty: 1, sent: false });
    }

    this.syncCurrentQty(line);
    this.showStepper(line);
    this.renderTotals();
    this.requestSync({ openDrawer: !this._drawerShown });
  }

  showStepper(line) {
    if (!line.stepperEl) {
      const stepper = document.createElement('div');
      stepper.className = 'bundle-stepper';

      const minus = document.createElement('button');
      minus.type = 'button';
      minus.className = 'bundle-stepper__btn';
      minus.textContent = '−';
      minus.setAttribute('aria-label', '−');

      const qty = document.createElement('span');
      qty.className = 'bundle-stepper__qty';

      const plus = document.createElement('button');
      plus.type = 'button';
      plus.className = 'bundle-stepper__btn';
      plus.textContent = '+';
      plus.setAttribute('aria-label', '+');

      minus.addEventListener('click', () => this.changeQty(line, -1));
      plus.addEventListener('click', () => this.changeQty(line, 1));

      stepper.append(minus, qty, plus);
      line.stepperEl = stepper;
      line.qtyEl = qty;
      line.actionEl.appendChild(stepper);
    }

    line.qtyEl.textContent = line.qty;
    if (line.addBtn) line.addBtn.hidden = true;
    this.renderNotify(line, null);
    line.stepperEl.hidden = false;
  }

  hideStepper(line) {
    if (line.stepperEl) line.stepperEl.hidden = true;
    if (line.addBtn) line.addBtn.hidden = false;
    // `updateAddState` tranchera entre l'ajout et l'alerte selon la variante courante.
    this.updateAddState(line);
  }

  changeQty(line, delta) {
    const entree = line.variant ? this.entryFor(line, line.variant.id) : null;
    if (!entree) return;
    this.changeEntryQty(line, entree, delta);
  }

  /** Fait varier la quantité d'une combinaison, qu'elle soit affichée ou dans le récapitulatif. */
  changeEntryQty(line, entree, delta) {
    entree.qty = Math.max(0, entree.qty + delta);
    if (entree.qty === 0) this.removeEntry(line, entree.variant.id);

    this.syncCurrentQty(line);

    if (line.qty === 0) {
      this.hideStepper(line);
    } else if (line.qtyEl) {
      line.qtyEl.textContent = line.qty;
    }

    this.renderTotals();
    this.requestSync();
  }


  renderTotals() {
    let count = 0;
    let total = 0;
    let compareTotal = 0;

    // Le total additionne toutes les combinaisons de chaque composant, pas seulement celle
    // que ses sélecteurs affichent.
    this.lines.forEach((line) => {
      line.entries.forEach((entree) => {
        if (entree.qty <= 0) return;

        count += entree.qty;
        total += entree.variant.price * entree.qty;

        const base =
          entree.variant.compareAt && entree.variant.compareAt > entree.variant.price
            ? entree.variant.compareAt
            : entree.variant.price;
        compareTotal += base * entree.qty;
      });
    });

    if (this.countEl) {
      let form = 'other';
      try {
        form = new Intl.PluralRules(this.locale).select(count);
      } catch (e) {
        form = count === 1 ? 'one' : 'other';
      }
      const tpl = this.countForms[form] || this.countForms.other || this.countForms.one;
      this.countEl.textContent = tpl.replace('{count}', count);
    }

    if (this.totalEl) {
      this.totalEl.textContent = count > 0 ? this.formatMoney(total) : '';
    }

    if (this.compareEl) {
      if (compareTotal > total) {
        this.compareEl.textContent = this.formatMoney(compareTotal);
        this.compareEl.hidden = false;
      } else {
        this.compareEl.textContent = '';
        this.compareEl.hidden = true;
      }
    }

    if (this.submitEl) {
      this.submitEl.disabled = count === 0;
    }
  }

  formatMoney(cents) {
    const value = cents / 100;
    const currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';

    try {
      return value.toLocaleString('fr-FR', { style: 'currency', currency: currency });
    } catch (e) {
      return value.toFixed(2).replace('.', ',') + ' €';
    }
  }

  /** Racine des routes (préfixe de langue / marché inclus). */
  get cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /**
   * Sections liées au panier à faire re-rendre côté serveur : le cart drawer
   * et le compteur se signalent eux-mêmes (protocole natif Stretch).
   */
  bundledSections() {
    const sections = new Set();
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sections },
      })
    );
    return [...sections].join(',');
  }

  async postCart(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      // Shopify explique son refus dans le corps de la réponse — « un article seulement en raison
      // de la disponibilité » quand le stock ne suit pas, par exemple. Sans ce message, l'échec
      // est muet : la quantité revient à sa valeur d'avant sans que rien ne le dise.
      let message = null;
      try {
        const erreur = await response.json();
        message = erreur.description || erreur.message || null;
      } catch (e) {
        /* réponse illisible : on s'en tient à l'échec brut */
      }

      const echec = new Error('Bundle cart request failed: ' + url);
      echec.cartMessage = message;
      throw echec;
    }

    return response.json();
  }

  async fetchCart() {
    return (await fetch(this.cartRoot + 'cart.js')).json();
  }

  /**
   * Aligne le builder sur la composition réellement au panier : au chargement
   * (éditer la composition existante plutôt qu'en créer une seconde) et après
   * un échec d'écriture, pour que l'écran ne mente pas sur le panier.
   * Silencieux en cas d'échec — le builder reste utilisable tel quel.
   */
  async restoreFromCart() {
    if (!this.bundleTitle) return;

    try {
      const cart = await this.fetchCart();
      const mine = cart.items.filter(
        (item) =>
          item.properties && item.properties._bundle_id && item.properties._bundle === this.bundleTitle
      );

      // Si plusieurs compositions coexistent (paniers antérieurs, 2e onglet),
      // on reprend la main sur la plus récente.
      const bundleId = mine.length ? mine[mine.length - 1].properties._bundle_id : null;
      const items = mine.filter((item) => item.properties._bundle_id === bundleId);

      // On repart du panier, pas de l'état affiché : ce qui n'y est plus disparaît du composeur.
      this.lines.forEach((line) => {
        line.entries = [];
      });

      // Plusieurs lignes de panier peuvent désormais retomber sur le même composant — c'est
      // tout l'objet du cumul. Elles s'y empilent au lieu de se disputer la ligne.
      let reprises = 0;
      items.forEach((item) => {
        const line = this.lines.find(
          (l) => l.addBtn && l.data.variants.some((v) => v.id === item.variant_id)
        );
        if (!line) return;

        const variant = line.data.variants.find((v) => v.id === item.variant_id);
        if (!variant.available) return;

        line.entries.push({ variant: variant, qty: item.quantity, sent: true });
        reprises += 1;
      });

      // Les sélecteurs se calent sur la dernière combinaison reprise ; les autres se lisent
      // dans le récapitulatif. `resolveVariant` réaligne au passage quantité et boutons.
      this.lines.forEach((line) => {
        const derniere = line.entries[line.entries.length - 1];

        if (derniere) {
          line.selects.forEach((select) => {
            const pos = parseInt(select.dataset.optionPosition, 10) - 1;
            if (pos >= 0 && derniere.variant.options[pos] != null) {
              select.value = derniere.variant.options[pos];
            }
          });
        }

        this.resolveVariant(line);
      });

      if (reprises) {
        this.bundleId = bundleId;
        // Une composition est déjà au panier : l'utilisateur l'a forcément vue,
        // le prochain ajout n'a pas à rouvrir le drawer.
        this._drawerShown = true;
      }
      this.selectWidgets.forEach((w) => this.syncTrigger(w));
      this.renderTotals();
      this.updateSubmitLabel();
    } catch (e) {
      /* panier injoignable : on garde le builder vierge */
    }
  }

  /** « Ajouter » tant que rien n'est au panier, « mettre à jour » ensuite. */
  updateSubmitLabel() {
    if (!this.submitEl) return;

    const ds = this.submitEl.dataset;
    const label = this.bundleId ? ds.labelUpdate : ds.labelAdd;
    if (label) this.submitEl.textContent = label;
  }

  /* --------------------------- Cover par couleur --------------------------- */

  get gallery() {
    if (this._gallery === undefined) {
      this._gallery = document.querySelector('product-gallery');
    }
    return this._gallery;
  }

  /** Comparaison insensible à la casse, aux accents et aux espaces multiples. */
  normalizeColor(value) {
    return String(value || '')
      .toUpperCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Rapproche une valeur d'option (`ANTHRACITE`) d'un libellé lu dans un alt
   * (`GRIS ANTHRACITE`). Exact d'abord ; à défaut par inclusion, mais seulement
   * si un seul libellé correspond — en cas d'ambiguïté on préfère ne rien
   * afficher plutôt que la mauvaise couleur (le catalogue compte quatre roses).
   */
  matchColorKey(keys, value) {
    const target = this.normalizeColor(value);
    if (!target) return null;

    const exact = keys.find((k) => this.normalizeColor(k) === target);
    if (exact) return exact;

    const loose = keys.filter((k) => {
      const n = this.normalizeColor(k);
      return n.includes(target) || target.includes(n);
    });
    return loose.length === 1 ? loose[0] : null;
  }

  /**
   * Affiche la couleur choisie dans la galerie produit. Dans l'ordre :
   * un visuel de la parure taggé à cette couleur (sélection native du
   * carrousel), sinon celui du composant (on prête la première cellule),
   * sinon on restaure la cover d'origine.
   */
  showCover(colorValue, line) {
    const gallery = this.gallery;
    if (!gallery) return;

    const parureKey = this.matchColorKey(Object.keys(this.coverMedia), colorValue);
    if (parureKey) {
      this.restoreCover();
      this.selectGalleryMedia(this.coverMedia[parureKey]);
      return;
    }

    const images = (line && line.data && line.data.colorImages) || {};
    const componentKey = this.matchColorKey(Object.keys(images), colorValue);
    if (componentKey) {
      const label = (line.data.label || '') + ' — ' + colorValue;
      this.borrowFirstCell(images[componentKey], label);
      return;
    }

    this.restoreCover();
  }

  /** Positionne le carrousel sur le média d'id donné. */
  selectGalleryMedia(mediaId) {
    const gallery = this.gallery;
    const cell = gallery.querySelector('[data-media-id="' + mediaId + '"]');
    if (!cell) return;

    const carousel = gallery.carousel;
    if (carousel && typeof carousel.select === 'function') {
      const index = [...carousel.cells].indexOf(cell);
      if (index >= 0) {
        carousel.select(index, { instant: true });
        return;
      }
    }
    cell.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  get firstCellImage() {
    const gallery = this.gallery;
    const cell = gallery && gallery.querySelector('[data-media-id]');
    return cell ? cell.querySelector('img') : null;
  }

  /**
   * Aucun visuel de parure pour cette couleur : on prête la première cellule
   * de la galerie à l'image du composant. Les attributs d'origine sont
   * mémorisés pour pouvoir rendre la cellule intacte.
   */
  borrowFirstCell(url, altLabel) {
    const img = this.firstCellImage;
    if (!img || !url) return;

    if (!this._coverOriginal) {
      this._coverOriginal = { src: img.getAttribute('src'), srcset: img.getAttribute('srcset'), alt: img.getAttribute('alt') };
    }

    img.setAttribute('src', this.imageUrlAt(url, 1200));
    // Le srcset d'origine pointe l'image de la parure : le laisser en place
    // laisserait le navigateur réafficher celle-ci selon la largeur.
    img.setAttribute(
      'srcset',
      [400, 600, 800, 1000, 1200].map((w) => this.imageUrlAt(url, w) + ' ' + w + 'w').join(', ')
    );
    img.setAttribute('alt', altLabel || '');

    this.selectFirstCell();
  }

  /** Rend la première cellule à la parure si on la lui avait empruntée. */
  restoreCover() {
    const original = this._coverOriginal;
    if (!original) return;

    const img = this.firstCellImage;
    if (img) {
      ['src', 'srcset', 'alt'].forEach((attr) => {
        if (original[attr] == null) img.removeAttribute(attr);
        else img.setAttribute(attr, original[attr]);
      });
    }
    this._coverOriginal = null;
  }

  selectFirstCell() {
    const carousel = this.gallery && this.gallery.carousel;
    if (carousel && typeof carousel.select === 'function') carousel.select(0, { instant: true });
  }

  /** Réécrit le paramètre `width` d'une URL de CDN Shopify. */
  imageUrlAt(url, width) {
    return String(url).replace(/([?&])width=\d+/, '$1width=' + width);
  }

  /** Feedback discret pendant qu'une écriture panier est en vol. */
  setSyncing(on) {
    this.classList.toggle('is-syncing', !!on);
  }

  /**
   * Demande une synchronisation du panier. Les clics rapprochés sont regroupés
   * (debounce) puis les envois sont chaînés, pour qu'une rafale sur « + » ne
   * produise ni requêtes concurrentes ni quantités fausses.
   */
  requestSync(options) {
    if ((options || {}).openDrawer) this._pendingOpenDrawer = true;

    this._syncPending = true;
    this.setSyncing(true);

    clearTimeout(this._syncTimer);
    this._syncTimer = setTimeout(() => {
      this._syncTimer = null;
      this._syncPending = false;

      const openDrawer = this._pendingOpenDrawer;
      this._pendingOpenDrawer = false;

      this._syncChain = this._syncChain
        .catch(() => {})
        .then(() => this.syncCart({ openDrawer: openDrawer }))
        .then(() => {
          // Un nouveau clic est peut-être déjà en attente : on ne retire
          // l'indicateur que si la file est réellement vide.
          if (!this._syncPending) this.setSyncing(false);
        });
    }, BUNDLE_SYNC_DEBOUNCE_MS);
  }

  /** Le bouton global force une synchronisation et ouvre le panier. */
  addToCart() {
    this.requestSync({ openDrawer: true });
  }

  /**
   * Réconcilie le panier avec la composition affichée : met à jour les lignes
   * du groupe déjà présentes (0 = retrait) et ajoute celles qui manquent.
   */
  async syncCart(options) {
    const openDrawer = !!(options || {}).openDrawer;
    // Une ligne pouvant porter plusieurs combinaisons, on raisonne sur la liste à plat des
    // couples (composant, combinaison) plutôt que sur les lignes.
    const selected = [];
    this.lines.forEach((line) => {
      line.entries.forEach((entree) => {
        if (entree.qty > 0) selected.push({ line: line, entree: entree });
      });
    });

    // Rien à composer et aucun groupe au panier : pas d'aller-retour inutile.
    if (!selected.length && !this.bundleId) return;

    if (this.submitEl) this.submitEl.disabled = true;
    const sections = this.bundledSections();

    // L'identifiant de groupe est conservé d'un envoi à l'autre : un second
    // clic met à jour la composition déjà au panier au lieu d'en créer une
    // deuxième.
    this.bundleId =
      this.bundleId ||
      (window.crypto && typeof window.crypto.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : 'bundle-' + Date.now());

    try {
      const cartBefore = await this.fetchCart();
      const existing = cartBefore.items.filter(
        (item) => item.properties && item.properties._bundle_id === this.bundleId
      );

      // Le client peut avoir retiré une pièce depuis le panier, sans que le composeur en sache
      // rien : il la croirait encore choisie et la remettrait au prochain envoi. Une ligne déjà
      // partie au panier mais absente de la composition en a donc été sortie volontairement, et
      // c'est le panier qui fait foi.
      const presentes = new Set(existing.map((item) => item.variant_id));
      selected.forEach((choix) => {
        if (choix.entree.sent && !presentes.has(choix.entree.variant.id)) {
          this.removeEntry(choix.line, choix.entree.variant.id);
          this.syncCurrentQty(choix.line);
          this.updateAddState(choix.line);
        }
      });

      const retenues = [];
      this.lines.forEach((line) => {
        line.entries.forEach((entree) => {
          if (entree.qty > 0) retenues.push({ line: line, entree: entree });
        });
      });

      // Quantité voulue par variante pour la composition en cours.
      const wanted = new Map();
      retenues.forEach((choix) => {
        const id = choix.entree.variant.id;
        wanted.set(id, (wanted.get(id) || 0) + choix.entree.qty);
      });

      // Lignes déjà au panier : nouvelle quantité, ou 0 si retirée de la
      // composition. Les quantités inchangées sont omises — sans ça, la moindre
      // synchro réécrirait tout le groupe et re-rendrait les sections pour rien.
      const updates = {};
      existing.forEach((item) => {
        const qty = wanted.get(item.variant_id) || 0;
        if (qty !== item.quantity) updates[item.key] = qty;
        wanted.delete(item.variant_id);
      });

      // L'éco-participation se lit sur le composant, pas sur la parure : on la retrouve par la
      // ligne qui porte la variante. Même libellé et même montant que `ecotax-property`, pour
      // qu'une parure et un achat à l'unité produisent la même mention sur la commande.
      const ecotaxParVariante = new Map();
      retenues.forEach((choix) => {
        if (choix.line.data.ecotaxLabel && choix.line.data.ecotaxAmount) {
          ecotaxParVariante.set(choix.entree.variant.id, choix.line.data);
        }
      });

      // Ce qui reste = variantes pas encore présentes dans ce groupe.
      const toAdd = [...wanted.entries()].map((entry) => {
        const properties = { _bundle_id: this.bundleId, _bundle: this.bundleTitle };
        const ecotax = ecotaxParVariante.get(entry[0]);
        if (ecotax) properties[ecotax.ecotaxLabel] = ecotax.ecotaxAmount;

        return { id: entry[0], quantity: entry[1], properties: properties };
      });

      const hasUpdates = Object.keys(updates).length > 0;

      // Le panier est déjà conforme (clic « + » puis « − » regroupés, ou
      // simple ouverture du panier depuis le bouton global).
      if (!hasUpdates && !toAdd.length) {
        if (openDrawer) this.openCart(cartBefore);
        if (this.submitEl) this.submitEl.disabled = false;
        return;
      }

      let lastResponse = null;
      if (hasUpdates) {
        lastResponse = await this.postCart(this.cartUpdateUrl, {
          updates: updates,
          sections: sections,
        });
      }
      if (toAdd.length > 0) {
        lastResponse = await this.postCart(this.cartAddUrl, { items: toAdd, sections: sections });
      }

      retenues.forEach((choix) => {
        choix.entree.sent = true;
      });

      const settings = (window.themeVariables && window.themeVariables.settings) || {};

      // Panier en mode page (ou déjà sur /cart) : on garde la redirection, mais
      // seulement sur une action explicite — une synchro de stepper ne doit pas
      // arracher l'utilisateur à la page produit.
      if (openDrawer && (settings.cartType === 'page' || settings.pageType === 'cart')) {
        window.location = this.cartUrl;
        return;
      }

      const cart = await this.fetchCart();
      cart.sections = (lastResponse && lastResponse.sections) || {};

      // Signal « ajout au panier » (tracking) : uniquement quand des articles
      // sont réellement entrés au panier, jamais sur un retrait.
      if (toAdd.length > 0) {
        const bundleItems = cart.items.filter(
          (item) => item.properties && item.properties._bundle_id === this.bundleId
        );

        this.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: { items: bundleItems, cart: cart },
          })
        );
      }

      // `baseEvent: 'variant:add'` ouvre le cart drawer ; `line-item:change`
      // se contente de rafraîchir son contenu et le compteur du header.
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: openDrawer ? 'variant:add' : 'line-item:change', cart: cart },
        })
      );

      if (openDrawer) this._drawerShown = true;

      this.updateSubmitLabel();
      if (this.submitEl) this.submitEl.disabled = false;
    } catch (e) {
      if (this.submitEl) this.submitEl.disabled = false;

      if (e && e.cartMessage) {
        document.dispatchEvent(
          new CustomEvent('toast:show', { detail: { message: e.cartMessage, tone: 'error' } })
        );
      }

      // L'écran ne doit pas mentir sur ce qui est réellement au panier.
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      await this.restoreFromCart();
    }
  }

  /** Ouvre le panier sans rien écrire (composition déjà conforme). */
  openCart(cart) {
    const settings = (window.themeVariables && window.themeVariables.settings) || {};

    if (settings.cartType === 'page' || settings.pageType === 'cart') {
      window.location = this.cartUrl;
      return;
    }

    this._drawerShown = true;
    const drawer = document.querySelector('cart-drawer');
    // Le drawer ne s'ouvre sur `cart:change` qu'avec des sections re-rendues :
    // sans écriture panier on n'en a pas, on l'ouvre donc directement.
    if (drawer && typeof drawer.show === 'function') drawer.show();
  }
}

if (!window.customElements.get('bundle-builder')) {
  window.customElements.define('bundle-builder', BundleBuilder);
}